/**
 * Form-specific button component for integration settings
 *
 * Extends uo-button but overrides click handling for form integration. This component
 * provides form-specific functionality including confirmation dialogs, row submissions,
 * and integration with the centralized event system.
 *
 * Features:
 * - Form submission integration with REST API
 * - Confirmation dialog support
 * - Row submission support for table rows
 * - Loading state management
 * - Integration ID override support
 * - Centralized event system integration
 *
 * @example
 * ```html
 * <uap-app-integration-settings-button
 *     type="submit"
 *     name="automator_action"
 *     value="save_settings"
 *     needs-confirmation
 *     confirmation-heading="Save Settings"
 *     confirmation-content="Are you sure you want to save these settings?">
 *     Save Settings
 * </uap-app-integration-settings-button>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsButton_rowSubmission_accessor_storage, _AutomatorAppIntegrationSettingsButton_integrationId_accessor_storage, _AutomatorAppIntegrationSettingsButton_confirmationFields_accessor_storage;
import { AutomatorButton } from '@automator/shared/components/button';
import { customElement, property } from 'lit/decorators.js';
import { dispatchEvent } from '../../utils/event-manager';
let AutomatorAppIntegrationSettingsButton = class AutomatorAppIntegrationSettingsButton extends AutomatorButton {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsButton_rowSubmission_accessor_storage.set(this, false);
        _AutomatorAppIntegrationSettingsButton_integrationId_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsButton_confirmationFields_accessor_storage.set(this, '');
    }
    /**
     * Whether this button should submit row-specific data when inside a table row
     *
     * @type {boolean}
     */
    get rowSubmission() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsButton_rowSubmission_accessor_storage, "f"); }
    set rowSubmission(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsButton_rowSubmission_accessor_storage, value, "f"); }
    /**
     * Integration ID override for this button (allows Pro/third-party overrides)
     *
     * @type {string}
     */
    get integrationId() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsButton_integrationId_accessor_storage, "f"); }
    set integrationId(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsButton_integrationId_accessor_storage, value, "f"); }
    /**
     * Form fields to include in confirmation dialog (JSON string)
     *
     * @type {string}
     */
    get confirmationFields() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsButton_confirmationFields_accessor_storage, "f"); }
    set confirmationFields(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsButton_confirmationFields_accessor_storage, value, "f"); }
    /**
     * Override the click handler to handle form-specific logic
     *
     * @param {PointerEvent} event - The click event
     *
     * @return {Promise<void>}
     */
    async _handleClick(event) {
        // If the button is disabled or loading, prevent the default behavior
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        // Handle form actions (both button and submit types)
        await this._handleFormButtonClick(event);
    }
    /**
     * Handle form button clicks with confirmation and form submission
     *
     * @param {PointerEvent} event - The click event
     *
     * @return {Promise<void>}
     */
    async _handleFormButtonClick(event) {
        // Handle confirmation if needed
        let postValues;
        if (this.needsConfirmation) {
            // Parse confirmation fields if provided
            let formFields = [];
            if (this.confirmationFields) {
                try {
                    formFields = JSON.parse(this.confirmationFields);
                }
                catch {
                    console.warn('Invalid confirmation fields JSON:', this.confirmationFields);
                }
            }
            // Create a fresh confirm dialog component each time to avoid state corruption
            const confirmDialog = document.createElement('uap-app-integration-settings-confirm-dialog');
            document.body.appendChild(confirmDialog);
            try {
                const config = {
                    heading: this.confirmationHeading,
                    content: this.confirmationContent,
                    confirmButtonLabel: this.confirmationButtonLabel,
                    formFields: formFields
                };
                const result = await confirmDialog.show(config);
                // Store form data from confirmation if available
                if (result.formData) {
                    postValues = result.formData;
                }
            }
            catch {
                // User cancelled confirmation
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            finally {
                // Always clean up the dialog element
                confirmDialog.remove();
            }
        }
        // Set loading state
        this.loading = true;
        try {
            // Prepare event data.
            const eventData = this.prepareEventData(postValues);
            // Add row submission data if this is a row submission
            let rowSubmission = false;
            if (this.rowSubmission) {
                const rowEl = this.closest('[data-row-id]');
                const rowId = rowEl?.getAttribute('data-row-id') || null;
                if (rowId && rowEl) {
                    rowSubmission = {
                        rowId: rowId,
                        rowElement: rowEl
                    };
                }
            }
            // Dispatch centralized event
            dispatchEvent('button-click', eventData, rowSubmission);
        }
        catch (error) {
            console.error('Button action error:', error);
            // Dispatch error event
            dispatchEvent('button-error', { error: error }, false);
        }
        finally {
            this.loading = false;
        }
    }
    /**
     * Prepare event data for form submission
     *
     * @param {Record<string, string | string[]>} [postValues] - Form data from confirmation dialog
     *
     * @return {ButtonClickEventData} The prepared event data object
     */
    prepareEventData(postValues) {
        return {
            button: this,
            name: this.name,
            value: this.value,
            type: this.type,
            integrationId: this.getIntegrationId(),
            postValues: postValues
        };
    }
    /**
     * Get the integration ID for this button
     *
     * @return {string} The integration ID
     */
    getIntegrationId() {
        // Priority: 1. integrationId property, 2. integration-id attribute
        return this.integrationId || this.getAttribute('integration-id') || '';
    }
};
_AutomatorAppIntegrationSettingsButton_rowSubmission_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsButton_integrationId_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsButton_confirmationFields_accessor_storage = new WeakMap();
__decorate([
    property({ type: Boolean, attribute: 'row-submission' })
], AutomatorAppIntegrationSettingsButton.prototype, "rowSubmission", null);
__decorate([
    property({ type: String, attribute: 'integration-id' })
], AutomatorAppIntegrationSettingsButton.prototype, "integrationId", null);
__decorate([
    property({ type: String, attribute: 'confirmation-fields' })
], AutomatorAppIntegrationSettingsButton.prototype, "confirmationFields", null);
AutomatorAppIntegrationSettingsButton = __decorate([
    customElement('uap-app-integration-settings-button')
    /**
     * @element uap-app-integration-settings-button
     * @summary A button component specifically designed for integration settings forms.
     *
     * This component extends the base uo-button functionality but overrides the click
     * handling to work with the integration settings form wrapper. It inherits all
     * properties and styling from uo-button.
     *
     * @slot - The default slot contains the button's label/content (text, icons, etc.).
     *
     * @attr {boolean} [disabled=false] - Disables the button, preventing interaction.
     * @attr {boolean} [loading=false] - Shows a loading indicator, also disables the button.
     * @attr {'secondary' | 'danger' | 'success' | 'transparent'} [color] - Sets the color scheme of the button.
     * @attr {boolean} [filled=false] - Applies a filled style variant.
     * @attr {'small' | 'extra-small'} [size] - Sets the size variant of the button.
     * @attr {'submit' | 'button'} [type] - The functional type of the button.
     * @attr {string} [name] - The name associated with the button, used during form submission.
     * @attr {string} [value] - The value associated with the button, used during form submission.
     * @attr {boolean} [needs-confirmation=false] - If true, displays a confirmation dialog before proceeding.
     * @attr {string} [confirmation-heading] - Heading text for the confirmation dialog.
     * @attr {string} [confirmation-content] - Content text for the confirmation dialog.
     * @attr {string} [confirmation-button-label] - Label for the confirm button in the dialog.
     * @attr {boolean} [row-submission=false] - If true, submits only row-specific data when inside a table row.
     * @attr {string} [integration-id] - Integration ID override for this button (allows Pro/third-party overrides).
     * @attr {string} [confirmation-fields] - JSON string of form fields to include in confirmation dialog.
     */
], AutomatorAppIntegrationSettingsButton);
export { AutomatorAppIntegrationSettingsButton };
